import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface PendingRefund {
  id: string;
  org_id: string;
  org_name: string;
  tracking_number: string;
  refund_amount: string;
  requested_at: string;
  carrier: string;
}

export const AdminDashboard: React.FC = () => {
  const [pendingRefunds, setPendingRefunds] = useState<PendingRefund[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [selectedRefunds, setSelectedRefunds] = useState<string[]>([]);
  const [uploadedCredits, setUploadedCredits] = useState<Set<string>>(new Set());

  const adminToken = localStorage.getItem('admin_token');
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      window.location.href = '/admin/login';
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${adminToken}`,
      'Content-Type': 'application/json',
    },
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [refundsRes, statsRes] = await Promise.all([
        api.get('/api/admin/refunds/pending'),
        api.get('/api/admin/stats'),
      ]);

      setPendingRefunds(refundsRes.data.refunds || []);
      setStats(statsRes.data.stats || {});
    } catch (error: any) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin/login';
  };

  const handleUploadUSPSFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      parseUSPSCredits(text);
    };
    reader.readAsText(file);
  };

  const parseUSPSCredits = (csvText: string) => {
    // Parse CSV and extract tracking numbers from USPS credits
    const lines = csvText.split('\n');
    const trackingNumbers = new Set<string>();

    for (const line of lines) {
      // Look for tracking numbers (various formats)
      // USPS tracking: 9400, 9205, 9270, 9303, etc. (20-22 digits)
      const matches = line.match(/\b(9[0-9]{19,21})\b/g);
      if (matches) {
        matches.forEach(tn => trackingNumbers.add(tn));
      }
    }

    if (trackingNumbers.size === 0) {
      alert('No USPS tracking numbers found in file. Please upload your USPS account statement CSV.');
      return;
    }

    // Find matching refunds
    const matchedRefunds = pendingRefunds.filter(r =>
      trackingNumbers.has(r.tracking_number)
    );

    if (matchedRefunds.length === 0) {
      alert(`Found ${trackingNumbers.size} tracking numbers but none match pending refunds.`);
      return;
    }

    // Auto-select matched refunds
    const matchedIds = matchedRefunds.map(r => r.id);
    setSelectedRefunds(matchedIds);
    setUploadedCredits(trackingNumbers);

    alert(`✅ Matched ${matchedRefunds.length} refunds from USPS file!\n\nThese refunds have been auto-selected. Review and click "Approve Selected" to credit customer wallets.`);
  };

  const handleApproveSelected = async () => {
    if (selectedRefunds.length === 0) {
      alert('No refunds selected');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Approve ${selectedRefunds.length} refunds and credit customer wallets?\n\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/admin/refunds/bulk-approve', {
        refund_ids: selectedRefunds,
      });

      const { approved, failed } = response.data;

      let message = `✅ Success!\n\nApproved: ${approved.length}\nFailed: ${failed.length}`;

      if (failed.length > 0) {
        message += `\n\nFailed refunds:\n${failed.map((f: any) => `- ${f.id}: ${f.reason}`).join('\n')}`;
      }

      alert(message);

      // Reload data
      setSelectedRefunds([]);
      setUploadedCredits(new Set());
      await loadData();
    } catch (error: any) {
      alert(`Failed to approve refunds: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await api.get('/api/admin/refunds/export', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `usps-pending-refunds-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      alert('Failed to download CSV');
    }
  };

  const toggleRefund = (id: string) => {
    setSelectedRefunds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const daysPending = (requestedAt: string) => {
    return Math.floor((Date.now() - new Date(requestedAt).getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">CraftyShip Admin Portal</h1>
              <p className="text-sm text-slate-600 mt-1">Secure administrative access</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{adminUser.full_name}</p>
                <p className="text-xs text-slate-600">{adminUser.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
            <p className="text-sm text-slate-600 mb-1">Total Organizations</p>
            <p className="text-3xl font-bold text-slate-900">{stats.total_orgs || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
            <p className="text-sm text-slate-600 mb-1">Pending Refunds</p>
            <p className="text-3xl font-bold text-orange-600">{stats.pending_refunds || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
            <p className="text-sm text-slate-600 mb-1">Pending Amount</p>
            <p className="text-3xl font-bold text-orange-600">
              ${parseFloat(stats.pending_refund_amount || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
            <p className="text-sm text-slate-600 mb-1">Total Wallet Balance</p>
            <p className="text-3xl font-bold text-green-600">
              ${parseFloat(stats.total_wallet_balance || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* USPS Refunds Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">USPS Refund Reconciliation</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Upload USPS account statement to auto-match and approve refunds
                </p>
              </div>
              <div className="flex gap-3">
                <label className="px-6 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 cursor-pointer">
                  📤 Upload USPS File
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleUploadUSPSFile}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={handleDownloadCSV}
                  className="px-6 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  💾 Download Pending CSV
                </button>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 text-xl">💡</div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">How to use:</p>
                <ol className="text-xs text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Download USPS account statement from PostalOne/Business Gateway</li>
                  <li>Click "Upload USPS File" and select the CSV/TXT file</li>
                  <li>CraftyShip will auto-match tracking numbers to pending refunds</li>
                  <li>Review auto-selected refunds below</li>
                  <li>Click "Approve Selected" to credit customer wallets</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Selection Actions */}
          {selectedRefunds.length > 0 && (
            <div className="px-6 py-4 bg-green-50 border-b border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {selectedRefunds.length} refund{selectedRefunds.length !== 1 ? 's' : ''} selected
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Total: ${pendingRefunds
                      .filter(r => selectedRefunds.includes(r.id))
                      .reduce((sum, r) => sum + parseFloat(r.refund_amount), 0)
                      .toFixed(2)}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedRefunds([])}
                    className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Clear Selection
                  </button>
                  <button
                    onClick={handleApproveSelected}
                    disabled={loading}
                    className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    ✓ Approve Selected ({selectedRefunds.length})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Refunds List */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRefunds.length === pendingRefunds.length && pendingRefunds.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRefunds(pendingRefunds.map(r => r.id));
                        } else {
                          setSelectedRefunds([]);
                        }
                      }}
                      className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Tracking Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Days Pending
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-600">
                      Loading refunds...
                    </td>
                  </tr>
                )}
                {!loading && pendingRefunds.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="text-6xl mb-4">✅</div>
                      <p className="text-lg text-slate-900 font-medium">No pending refunds</p>
                      <p className="text-sm text-slate-600 mt-2">All USPS refunds have been processed</p>
                    </td>
                  </tr>
                )}
                {!loading && pendingRefunds.map((refund) => {
                  const days = daysPending(refund.requested_at);
                  const isMatched = uploadedCredits.has(refund.tracking_number);

                  return (
                    <tr
                      key={refund.id}
                      className={`hover:bg-slate-50 ${isMatched ? 'bg-green-50' : ''} ${
                        selectedRefunds.includes(refund.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRefunds.includes(refund.id)}
                          onChange={() => toggleRefund(refund.id)}
                          className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-slate-900">{refund.org_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500 font-mono">{refund.org_id.substring(0, 8)}...</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-mono text-slate-900">{refund.tracking_number}</p>
                          {isMatched && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              ✓ In USPS File
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-slate-900">
                          ${parseFloat(refund.refund_amount).toFixed(2)}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className={`text-sm font-medium ${
                          days > 21 ? 'text-green-600' :
                          days > 14 ? 'text-orange-600' :
                          'text-slate-600'
                        }`}>
                          {days} days
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          Pending
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer Stats */}
          {!loading && pendingRefunds.length > 0 && (
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <div className="flex items-center justify-between text-sm">
                <div className="flex gap-6">
                  <div>
                    <span className="text-slate-600">Total Pending:</span>
                    <span className="ml-2 font-medium text-slate-900">{pendingRefunds.length}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Total Amount:</span>
                    <span className="ml-2 font-medium text-slate-900">
                      ${pendingRefunds.reduce((sum, r) => sum + parseFloat(r.refund_amount), 0).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-600">Oldest:</span>
                    <span className="ml-2 font-medium text-slate-900">
                      {Math.max(...pendingRefunds.map(r => daysPending(r.requested_at)))} days
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const over21Days = pendingRefunds.filter(r => daysPending(r.requested_at) > 21).map(r => r.id);
                    setSelectedRefunds(over21Days);
                    alert(`Selected ${over21Days.length} refunds that are 21+ days old (likely approved by USPS)`);
                  }}
                  className="px-4 py-2 text-sm font-medium text-brand-blue bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  Auto-Select 21+ Days
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
